/**
 * Parallax Utility Module
 * GSAPベースの再利用可能なパララックスモジュール
 *
 * 使用方法:
 * const parallax = new ParallaxUtility();
 *
 * HTML例:
 * <div class="js-parallax" data-y="1" data-scale="1.15">パララックス要素</div>
 * <div class="js-parallax--horizontal" data-x="2">横パララックス要素</div>
 *
 * 必須: GSAP と ScrollTrigger をCDNで事前に読み込む必要があります
 */

// GSAPとScrollTriggerはCDNから読み込まれるグローバル変数を使用
if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

/**
 * レスポンシブ値を取得するヘルパー関数
 * @param {number} pcValue - PC用の値
 * @param {number} spValue - SP用の値
 * @returns {number} - 現在のデバイスに応じた値
 */
function getResponsiveValue(pcValue, spValue) {
  return window.matchMedia('(max-width: 749px)').matches ? spValue : pcValue;
}

/**
 * デバイスの向きを取得
 * @returns {string} - 'portrait' または 'landscape'
 */
function getOrientation() {
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
}

/**
 * SPデバイスかどうか判定
 * @returns {boolean}
 */
function isSP() {
  return window.matchMedia('(max-width: 749px)').matches && getOrientation() === 'portrait';
}

/**
 * パララックス設定オブジェクト
 * @typedef {Object} ParallaxConfig
 * @property {string} verticalSelector - 縦方向パララックス要素のセレクター
 * @property {string} horizontalSelector - 横方向パララックス要素のセレクター
 */

class ParallaxUtility {
  /**
   * @param {ParallaxConfig} config - パララックス設定
   */
  constructor(config = {}) {
    this.config = {
      verticalSelector: '.js-parallax',
      horizontalSelector: '.js-parallax--horizontal',
      ...config,
    };

    this.triggers = document.querySelectorAll(this.config.verticalSelector);
    this.horizontalTriggers = document.querySelectorAll(this.config.horizontalSelector);
    this.scrollTriggers = [];

    this.init();
  }

  /**
   * 初期化
   */
  init() {
    this.initVerticalParallax();
    this.initHorizontalParallax();
  }

  /**
   * 縦方向パララックスの初期化
   */
  initVerticalParallax() {
    this.triggers.forEach((element) => {
      const currentIsSP = isSP();

      // デバイス制限のチェック
      const onlyPC = element.classList.contains('js-parallax--onlyPC');
      const onlySP = element.classList.contains('js-parallax--onlySP');

      if (currentIsSP && onlyPC) return;
      if (!currentIsSP && onlySP) return;

      // オプションの取得
      const options = this.getVerticalOptions(element, currentIsSP);

      // GSAPアニメーション設定
      gsap.set(element, { scale: options.scale });

      const scrollTrigger = gsap.fromTo(
        element,
        { y: -options.transformY * 2 },
        {
          y: 0,
          ease: 'power1.inOut',
          scrollTrigger: {
            trigger: element.parentElement,
            start: options.startTiming,
            end: options.long ? '200%' : 'bottom top',
            scrub: 1,
          },
        }
      );

      this.scrollTriggers.push(scrollTrigger);
    });
  }

  /**
   * 横方向パララックスの初期化
   */
  initHorizontalParallax() {
    this.horizontalTriggers.forEach((element) => {
      const currentIsSP = isSP();

      // デバイス制限のチェック
      const onlyPC = element.classList.contains('js-parallax--horizontal--onlyPC');
      const onlySP = element.classList.contains('js-parallax--horizontal--onlySP');

      if (currentIsSP && onlyPC) return;
      if (!currentIsSP && onlySP) return;

      // オプションの取得
      const options = this.getHorizontalOptions(element);

      // GSAPアニメーション設定
      const scrollTrigger = gsap.fromTo(
        element,
        { x: 0 },
        {
          x: -options.transformX * 2,
          ease: 'power1.inOut',
          scrollTrigger: {
            trigger: element.parentElement,
            start: 'top 150%',
            end: 'bottom -50%',
            scrub: 1,
          },
        }
      );

      this.scrollTriggers.push(scrollTrigger);
    });
  }

  /**
   * 縦方向パララックスのオプションを取得
   * @param {HTMLElement} element - 対象要素
   * @param {boolean} currentIsSP - SP表示かどうか
   * @returns {Object} オプション
   */
  getVerticalOptions(element, currentIsSP) {
    const startTiming = element.dataset.startY !== undefined ? element.dataset.startY : 'top 100%';
    const dataScalePC = parseFloat(element.dataset.scale) || 1.15;
    const dataScaleSP = parseFloat(element.dataset.scaleSp) || 1.15;
    const dataScale = currentIsSP ? dataScaleSP : dataScalePC;

    // data-distance属性で移動距離を直接指定
    const dataDistancePC = element.dataset.distance !== undefined ? parseFloat(element.dataset.distance) : null;
    const dataDistanceSP = element.dataset.distanceSp !== undefined ? parseFloat(element.dataset.distanceSp) : null;
    const dataDistance = currentIsSP ? dataDistanceSP : dataDistancePC;

    // data-distance-multiplier属性で倍数を指定（基準値は1）
    const dataMultiplierPC =
      element.dataset.distanceMultiplier !== undefined ? parseFloat(element.dataset.distanceMultiplier) : null;
    const dataMultiplierSP =
      element.dataset.distanceMultiplierSp !== undefined ? parseFloat(element.dataset.distanceMultiplierSp) : null;
    const dataMultiplier = currentIsSP ? dataMultiplierSP : dataMultiplierPC;

    let transformY;

    // 優先順位: data-distance > data-distance-multiplier
    if (dataDistance !== null) {
      // 直接指定された距離を使用
      transformY = dataDistance;
    } else if (dataMultiplier !== null) {
      // 倍数指定（基準値1pxの倍数）
      transformY = 10 * dataMultiplier;
    } else {
      // デフォルト値
      transformY = 10;
    }

    return {
      scale: dataScale,
      transformY,
      startTiming,
    };
  }

  /**
   * 横方向パララックスのオプションを取得
   * @param {HTMLElement} element - 対象要素
   * @returns {Object} オプション
   */
  getHorizontalOptions(element) {
    const currentIsSP = isSP();

    // data-distance属性で移動距離を直接指定
    const dataDistancePC = element.dataset.distance !== undefined ? parseFloat(element.dataset.distance) : null;
    const dataDistanceSP = element.dataset.distanceSp !== undefined ? parseFloat(element.dataset.distanceSp) : null;
    const dataDistance = currentIsSP ? dataDistanceSP : dataDistancePC;

    // data-distance-multiplier属性で倍数を指定（基準値は1）
    const dataMultiplierPC =
      element.dataset.distanceMultiplier !== undefined ? parseFloat(element.dataset.distanceMultiplier) : null;
    const dataMultiplierSP =
      element.dataset.distanceMultiplierSp !== undefined ? parseFloat(element.dataset.distanceMultiplierSp) : null;
    const dataMultiplier = currentIsSP ? dataMultiplierSP : dataMultiplierPC;

    let transformX;

    // 優先順位: data-distance > data-distance-multiplier
    if (dataDistance !== null) {
      // 直接指定された距離を使用
      transformX = dataDistance;
    } else if (dataMultiplier !== null) {
      // 倍数指定（基準値1pxの倍数）
      transformX = dataMultiplier;
    } else {
      // デフォルト値
      transformX = 1;
    }

    return { transformX };
  }

  /**
   * すべてのScrollTriggerを破棄
   */
  destroy() {
    this.scrollTriggers.forEach((trigger) => {
      if (trigger.scrollTrigger) {
        trigger.scrollTrigger.kill();
      }
    });
    this.scrollTriggers = [];
  }

  /**
   * リフレッシュ（ウィンドウリサイズ時など）
   */
  refresh() {
    ScrollTrigger.refresh();
  }

  /**
   * 再初期化
   */
  reinit() {
    this.destroy();
    this.triggers = document.querySelectorAll(this.config.verticalSelector);
    this.horizontalTriggers = document.querySelectorAll(this.config.horizontalSelector);
    this.init();
  }
}

// グローバルスコープに公開
window.ParallaxUtility = ParallaxUtility;
